// client/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// --- Layout & Global Components ---
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import { QuoteProvider } from './context/QuoteContext';
import QuoteModal from './components/quote/QuoteModal';
import ScrollToTop from './utils/ScrollToTop';
import ViewportManager from './utils/ViewportManager';
import Preloader from './components/layout/Preloader';
import CookieConsent from './components/layout/CookieConsent';
import WhatsAppButton from './components/layout/WhatsAppButton';

// --- Public Page Components ---
import HomePage from './components/pages/HomePage';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import ProductsPage from './components/pages/ProductsPage';
import ServicesPage from './components/pages/ServicesPage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './components/pages/TermsAndConditionsPage';

// --- Admin Components & Security ---
import AdminLoginPage from './components/pages/AdminLoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';
import QuoteManager from './components/admin/QuoteManager';
import ProductManager from './components/admin/ProductManager';
import MessageManager from './components/admin/MessageManager';

// --- Main Stylesheet ---
import './App.css';

// Animated route container that triggers smooth entrance transitions on upper-menu navigation
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <main className="main-route-stage" key={location.pathname}>
      <Routes location={location}>
        {/* === Public Routes === */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Service Pages */}
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:serviceSlug" element={<ServicesPage />} />

        {/* Product Catalog Pages */}
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:categorySlug" element={<ProductsPage />} />

        {/* Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />

        {/* === Admin Routes === */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="quotes" element={<QuoteManager />} />
          <Route path="products" element={<ProductManager />} />
          <Route path="messages" element={<MessageManager />} />
        </Route>
      </Routes>
    </main>
  );
};

function App() {
  const [showPreloader, setShowPreloader] = useState(true);
  const [isPreloaderExiting, setIsPreloaderExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsPreloaderExiting(true);
    }, 1350);

    const unmountTimer = setTimeout(() => {
      setShowPreloader(false);
    }, 1900);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(unmountTimer);
    };
  }, []);

  return (
    <QuoteProvider>
      {showPreloader && <Preloader isExiting={isPreloaderExiting} />}
      <Router>
        <ViewportManager />
        <ScrollToTop />
        <div className="App">
          <Header />
          <QuoteModal />
          <AnimatedRoutes />
          <Footer />
          <WhatsAppButton />
          <CookieConsent />
        </div>
      </Router>
    </QuoteProvider>
  );
}

export default App;
